import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useAuth } from '../../../contexts/AuthContext';
import { proposalApi, ProposalHistorySummary } from '../services/proposalApi';

interface ProposalHistorySelectorProps {
  onLoad: (proposalId: string, clientName: string) => void;
  onReset?: () => void;
  currentProposalId?: string;
  currentClientName?: string;
  refreshTrigger?: number;
  onNewProposal?: (item: ProposalHistorySummary) => void;
}

// Export a method to add a new proposal to history from outside
export interface ProposalHistorySelectorHandle {
  addProposal: (item: ProposalHistorySummary) => void;
}

export const ProposalHistorySelector = forwardRef<ProposalHistorySelectorHandle, ProposalHistorySelectorProps>(({
  onLoad,
  onReset,
  currentProposalId,
  currentClientName,
  refreshTrigger,
  onNewProposal
}, ref) => {
  const { getAccessToken } = useAuth();
  const [history, setHistory] = useState<ProposalHistorySummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletedProposalIds, setDeletedProposalIds] = useState<Set<string>>(new Set());
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }),
    []
  );

  const loadHistory = useCallback(async (forceRefresh = false) => {
    if (!getAccessToken) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token is unavailable');
      }

      const items = await proposalApi.listUserHistory(token!, {
        forceRefresh
      });

      if (isMountedRef.current) {
        items.sort((a, b) => {
          const aTime = (a.updatedAt || a.createdAt).getTime();
          const bTime = (b.updatedAt || b.createdAt).getTime();
          return bTime - aTime;
        });

        setHistory(items);

        // Clear deleted proposals tracker - if they appear in backend history, they're valid again
        setDeletedProposalIds(new Set());
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }
      console.error('Failed to load proposal history', err);
      const message = err instanceof Error ? err.message : 'Unable to load proposal history';
      setError(message);
      setHistory([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [getAccessToken]);

  // Method to add a newly created proposal to the history
  const addProposal = useCallback((item: ProposalHistorySummary) => {
    setHistory(prev => {
      const filtered = prev.filter(p => p.proposalId !== item.proposalId);
      return [item, ...filtered];
    });

    onNewProposal?.(item);
  }, [onNewProposal]);

  useImperativeHandle(ref, () => ({ addProposal }), [addProposal]);

  // Provide a global escape hatch so other modules (like the generator) can
  // enqueue history updates even if the selector is temporarily unmounted.
  useEffect(() => {
    (window as any).__proposalHistoryAddProposal = addProposal;
    return () => {
      if ((window as any).__proposalHistoryAddProposal === addProposal) {
        delete (window as any).__proposalHistoryAddProposal;
      }
    };
  }, [addProposal]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshTrigger]);

  // If we have a currentProposalId that's not in history, add a placeholder for it
  // This handles the case where a proposal is loaded from IndexedDB before backend history loads
  useEffect(() => {
    if (currentProposalId) {
      const exists = history.some(item => item.proposalId === currentProposalId);
      const wasDeleted = deletedProposalIds.has(currentProposalId);

      if (!exists && !wasDeleted) {
        // Add a placeholder item for the current proposal
        const placeholder: ProposalHistorySummary = {
          proposalId: currentProposalId,
          clientName: currentClientName || 'Current Proposal',
          clientSlug: 'current',
          createdAt: new Date(),
          updatedAt: new Date(),
          format: 'one-slider' as any,
          teamMemberCount: 0,
          clientUrl: ''
        };
        setHistory(prev => [placeholder, ...prev]);
      }
    }
  }, [currentProposalId, currentClientName, history, deletedProposalIds]);

  const handleStartNew = useCallback(() => {
    onReset?.();
  }, [onReset]);

  const handleLoad = useCallback(
    (item: ProposalHistorySummary) => {
      onLoad(item.proposalId, item.clientName);
    },
    [onLoad]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const proposalId = event.target.value;
      if (proposalId === '') {
        // Always reset when "Start new proposal" is selected, even if already showing empty
        handleStartNew();
      } else {
        const item = history.find(h => h.proposalId === proposalId);
        if (item) {
          handleLoad(item);
        }
      }
    },
    [history, handleLoad, handleStartNew]
  );

  const handleDelete = useCallback(
    async (proposalId: string) => {
      if (!getAccessToken) {
        return;
      }

      const confirmed = window.confirm('Delete this proposal from history? This cannot be undone.');
      if (!confirmed) {
        return;
      }

      try {
        setDeletingId(proposalId);
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Authentication token is unavailable');
        }

        // Delete from backend
        await proposalApi.deleteUserProposal(proposalId, token!);

        // IndexedDB disabled - no need to delete from it
        // await proposalPersistenceService.deleteProposal(proposalId);

        // Track this proposal as deleted to prevent resurrection
        setDeletedProposalIds(prev => new Set(prev).add(proposalId));

        // Only update UI state if both deletions succeeded
        setHistory(prev => prev.filter(item => item.proposalId !== proposalId));

        // Reset the view if currently viewing the deleted proposal
        if (currentProposalId === proposalId) {
          onReset?.();
        }
      } catch (err) {
        console.error('Failed to delete proposal', err);
        const message = err instanceof Error ? err.message : 'Unable to delete proposal';
        setError(message);

        // If deletion failed, reload history to ensure UI is in sync with backend
        loadHistory(true);
      } finally {
        setDeletingId(null);
      }
    },
    [currentProposalId, getAccessToken, onReset, loadHistory]
  );

  // Always show currentProposalId if it exists, even if not in history yet
  // This handles the case where a proposal is loaded from IndexedDB before history loads
  const selectValue = useMemo(() => {
    if (!currentProposalId) {
      return '';
    }

    const inHistory = history.some(item => item.proposalId === currentProposalId);
    return inHistory ? currentProposalId : '';
  }, [currentProposalId, history]);

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {error && <Alert severity="error">{error}</Alert>}

      {loading && history.length === 0 ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : history.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1} py={3}>
          <HistoryIcon color="disabled" fontSize="large" />
          <Typography variant="body2" color="text.secondary">
            No saved proposals yet. Generate a proposal and it will appear here.
          </Typography>
        </Box>
      ) : (
        <Box display="flex" gap={1} alignItems="center">
          <FormControl fullWidth size="small">
            <InputLabel id="proposal-history-label">Recent Proposals</InputLabel>
            <Select
              labelId="proposal-history-label"
              id="proposal-history-select"
              value={selectValue}
              label="Recent Proposals"
              onChange={handleSelectChange}
              disabled={loading}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="">
                <em>Start new proposal</em>
              </MenuItem>
              {history.map(item => {
                const profileCount = item.teamMemberCount ?? 0;
                const dateLabel = dateFormatter.format(item.updatedAt || item.createdAt);
                const secondaryText = `${dateLabel} • ${profileCount} profile${profileCount === 1 ? '' : 's'}`;

                return (
                  <MenuItem
                    key={item.proposalId}
                    value={item.proposalId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover .delete-button': {
                        opacity: 1
                      }
                    }}
                  >
                    <ListItemText
                      primary={item.clientName}
                      secondary={secondaryText}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontWeight: 500, color: 'text.primary' }
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary'
                      }}
                    />
                    <Tooltip title="Delete from history">
                      <IconButton
                        className="delete-button"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.proposalId);
                        }}
                        disabled={deletingId === item.proposalId}
                        sx={{
                          ml: 2,
                          opacity: 0.7,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        {deletingId === item.proposalId ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Tooltip title="Refresh history">
            <span>
              <IconButton
                size="small"
                onClick={() => loadHistory(true)}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
});

ProposalHistorySelector.displayName = 'ProposalHistorySelector';
